import {computedAsync, useDevicesList, useEventBus, usePermission, useUserMedia} from '@vueuse/core'
import {defineStore} from 'pinia'
import {computed, ref, shallowRef, watch} from 'vue'
import {androidConnector} from '@communication/remotes/AndroidConnector'

type Kind = 'video' | 'audio'

const {microphone, camera} = window.__INITIAL_STATE__.app.idealConstraints

const isRealDevice = (device: MediaDeviceInfo) =>
  device.deviceId && device.deviceId !== 'default' && device.deviceId !== 'communications'
const stopStream = (stream?: MediaStream | null) => stream?.getTracks().forEach(track => track.stop())

export const useStreamStore = defineStore('streamStore', () => {
  /* ------------------------------------------------------------------ *
   * State (refs)
   * ------------------------------------------------------------------ */

  // Camera
  const camEnabled = ref(false)
  const camLost = ref(false) // the track ended by itself (unplugged, OS privacy switch, taken by another app)
  const selectedCameraId = ref<string | undefined>(undefined)
  // Set while a snapshot needs the camera at its maximum resolution: the constraint change restarts the stream
  const camResolution = ref<{width: number; height: number} | null>(null)
  const cameraAccess = usePermission('camera')
  /**
   * We don't set torch value in constraint in order to reuse stream instead of creating new one
   */
  const torchToggled = ref(false)

  // Microphone
  const micEnabled = ref(false)
  const micLost = ref(false)
  const selectedMicId = ref<string | undefined>(undefined)
  const microphoneAccess = usePermission('microphone')

  // Android bridge (e.g. smart glasses): its devices are listed with the browser's, but captured through the bridge's
  // peer connection (androidConnector.getTrack) instead of getUserMedia
  const androidConnected = ref(false)
  const androidDevices = ref<{deviceId: string; kind: string; label: string}[]>([])
  const androidCamStream = shallowRef<MediaStream | undefined>(undefined)
  const androidMicStream = shallowRef<MediaStream | undefined>(undefined)

  /* ------------------------------------------------------------------ *
   * Devices
   * ------------------------------------------------------------------ */

  // Only the kinds the session recommends are requested: an undefined constraint means that device is not used.
  // The permission is not asked for when the store is created (useDevicesList would call getUserMedia at once) but by
  // App.vue once this page has the call (useCallTab): a page that waits for another one must not touch the camera
  const {videoInputs, audioInputs, ensurePermissions, permissionGranted} = useDevicesList({
    requestPermissions: false,
    constraints: {video: !!camera, audio: !!microphone},
  })

  const isAndroidDevice = (deviceId?: string) => !!deviceId && androidDevices.value.some(d => d.deviceId === deviceId)

  /* ------------------------------------------------------------------ *
   * Computed: devices and constraints
   * ------------------------------------------------------------------ */

  const cameras = computed(() => [
    ...videoInputs.value.filter(isRealDevice),
    ...androidDevices.value.filter(device => device.kind === 'videoinput'),
  ])
  const microphones = computed(() => [
    ...audioInputs.value.filter(isRealDevice),
    ...androidDevices.value.filter(device => device.kind === 'audioinput'),
  ])

  const androidCamSelected = computed(() => isAndroidDevice(selectedCameraId.value))
  const androidMicSelected = computed(() => isAndroidDevice(selectedMicId.value))

  const camConstraint = computed(() => ({
    video: {
      ...camera,
      ...(camResolution.value && {
        width: {ideal: camResolution.value.width},
        height: {ideal: camResolution.value.height},
        resizeMode: 'none',
      }),
      ...(selectedCameraId.value ? {deviceId: {exact: selectedCameraId.value}} : {}),
    },
  }))
  const micConstraint = computed(() => ({
    audio: {
      ...microphone,
      ...(selectedMicId.value ? {deviceId: {exact: selectedMicId.value}} : {}),
    },
  }))

  /* ------------------------------------------------------------------ *
   * Browser capture (getUserMedia)
   * ------------------------------------------------------------------ */

  const {
    stream: browserCamStream,
    start: startBrowserCam,
    restart: restartBrowserCam,
    isSupported: isCamSupported,
  } = useUserMedia({
    autoSwitch: true,
    constraints: camConstraint,
    enabled: computed(() => camEnabled.value && !androidCamSelected.value),
  })
  const {
    stream: browserMicStream,
    start: startBrowserMic,
    restart: restartBrowserMic,
    isSupported: isMicSupported,
  } = useUserMedia({
    autoSwitch: true,
    constraints: micConstraint,
    enabled: computed(() => micEnabled.value && !androidMicSelected.value),
  })

  /* ------------------------------------------------------------------ *
   * Computed: the stream and state each kind exposes
   * ------------------------------------------------------------------ */

  // Camera
  const camStream = computed(() => (androidCamSelected.value ? androidCamStream.value : browserCamStream.value))
  const camAvailable = computed(() => cameras.value.length > 0 && !camLost.value)
  const camOn = computed(() => isCamSupported.value && camEnabled.value && !!camStream.value)
  const camTrack = computed(() => camStream.value?.getVideoTracks()[0])
  const hasTorchCapability = computedAsync(async () => {
    const track = camTrack.value
    if (!track) return false
    // toggleTorch uses the `torch` constraint, so the track capability is the source of truth
    const trackCapabilities = track.getCapabilities()
    if ('torch' in trackCapabilities && trackCapabilities.torch) {
      return true
    }
    if (!('ImageCapture' in window)) {
      return false
    }
    try {
      const capabilities = await new window.ImageCapture(track).getPhotoCapabilities()
      return (capabilities?.fillLightMode?.length || 0) > 0
    } catch (e) {
      console.error(e)
      // getPhotoCapabilities is flaky on some devices
      return false
    }
  }, false)
  watch(hasTorchCapability, isAvailable => {
    if (isAvailable) {
      torchToggled.value = false
    }
  })
  // Microphone
  const micStream = computed(() => (androidMicSelected.value ? androidMicStream.value : browserMicStream.value))
  const micAvailable = computed(() => microphones.value.length > 0 && !micLost.value)
  const micOn = computed(() => isMicSupported.value && micEnabled.value && !!micStream.value)

  /* ------------------------------------------------------------------ *
   * Actions: Android bridge
   * ------------------------------------------------------------------ */

  const connectAndroid = async () => {
    androidConnected.value = !!(await androidConnector.connect())
    androidDevices.value = androidConnected.value ? [...(androidConnector.devices ?? [])] : []
    return androidConnected.value
  }

  const startAndroid = async (kind: Kind) => {
    const target = kind === 'video' ? androidCamStream : androidMicStream
    stopStream(target.value)
    // AndroidConnector.getTrack logs a failure and resolves undefined
    const track = await androidConnector.getTrack(kind)
    if (!track) throw new Error(`No ${kind} track from the Android device`)
    target.value = new MediaStream([track])
    return target.value
  }

  // The bridge's tracks follow the same enabled/selected state as the browser captures
  const syncAndroid = (kind: Kind, wanted: boolean) => {
    const target = kind === 'video' ? androidCamStream : androidMicStream
    if (!wanted) {
      stopStream(target.value)
      target.value = undefined
      return
    }
    startAndroid(kind).catch(error => {
      console.error(`[streamStore] Android ${kind} failed:`, error)
      target.value = undefined
    })
  }

  /* ------------------------------------------------------------------ *
   * Actions: start / restart / recover
   * ------------------------------------------------------------------ */

  const startCam = () => (androidCamSelected.value ? startAndroid('video') : startBrowserCam())
  const restartCam = () => (androidCamSelected.value ? startAndroid('video') : restartBrowserCam())
  const startMic = () => (androidMicSelected.value ? startAndroid('audio') : startBrowserMic())
  const restartMic = () => (androidMicSelected.value ? startAndroid('audio') : restartBrowserMic())

  // Ends the capture of both kinds (the call is over)
  const stopAll = () => {
    camEnabled.value = false
    micEnabled.value = false
  }

  // Recovery of a kind that was lost: a new capture on the selected device. A device that is not released yet
  // (NotReadableError/AbortError) is tried again a few times
  const recover = async (kind: Kind, delays = [1000, 2000]) => {
    const restart = kind === 'video' ? restartCam : restartMic
    const enabled = kind === 'video' ? camEnabled : micEnabled
    if (!enabled.value) return
    for (let attempt = 0; ; attempt++) {
      try {
        await restart()
        return
      } catch (error: any) {
        const retriable = error?.name === 'NotReadableError' || error?.name === 'AbortError'
        if (!retriable || attempt >= delays.length) throw error
        await new Promise(resolve => setTimeout(resolve, delays[attempt]))
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Actions: camera only (snapshot, torch)
   * ------------------------------------------------------------------ */

  // Wait for the next stream the constraints produce (a restart makes a new MediaStream)
  const nextCamStream = (timeout = 5000) =>
    new Promise<MediaStream | undefined>(resolve => {
      const timer = setTimeout(() => {
        stop()
        resolve(camStream.value)
      }, timeout)
      const stop = watch(camStream, stream => {
        if (!stream) return
        clearTimeout(timer)
        stop()
        resolve(stream)
      })
    })

  const toggleTorch = async () => {
    const track = camTrack.value
    if (!track) return false
    await track.applyConstraints({advanced: [{torch: !track.getSettings().torch} as any]})
    torchToggled.value = !!track.getSettings().torch
  }

  /* ------------------------------------------------------------------ *
   * Watches: selected device
   * ------------------------------------------------------------------ */

  // Keeps the selection on a connected device; a bridge device is preferred when there is no (valid) selection
  const watchSelection = (
    devices: typeof cameras | typeof microphones,
    selected: typeof selectedCameraId | typeof selectedMicId
  ) =>
    watch(devices, list => {
      if (list.length === 0) {
        selected.value = undefined
        return
      }
      if (!list.some(device => device.deviceId === selected.value)) {
        selected.value = (list.find(device => isAndroidDevice(device.deviceId)) ?? list[0]).deviceId
      }
    })
  watchSelection(cameras, selectedCameraId)
  watchSelection(microphones, selectedMicId)

  /* ------------------------------------------------------------------ *
   * Watches: Android bridge capture
   * ------------------------------------------------------------------ */

  watch([camEnabled, androidCamSelected], ([enabled, android]) => syncAndroid('video', enabled && android), {
    immediate: true,
  })
  watch([micEnabled, androidMicSelected], ([enabled, android]) => syncAndroid('audio', enabled && android), {
    immediate: true,
  })

  /* ------------------------------------------------------------------ *
   * Watches: stream acquired / track lost
   * ------------------------------------------------------------------ */

  // 'stream:track:start' tells that a stream is acquired; 'stream:track:end' hands the kind to the recovery
  // (useMediaRecovery) when a track ended by itself
  const watchEnded = (stream: MediaStream | undefined | null, kind: Kind) => {
    if (!stream) return
    const lost = kind === 'video' ? camLost : micLost
    const current = kind === 'video' ? camStream : micStream
    lost.value = false
    useEventBus('stream:track:start').emit({name: `${kind}_capture`, status: 'granted'})
    for (const track of stream.getTracks()) {
      track.addEventListener('ended', () => {
        // stop() (mute, device switch) fires no 'ended': only a loss gets here
        if (track.readyState !== 'ended' || stream !== current.value) return
        lost.value = true
        useEventBus('stream:track:end').emit({name: `${kind}_capture`, status: 'denied'})
      })
    }
  }
  watch(camStream, stream => watchEnded(stream, 'video'))
  watch(micStream, stream => watchEnded(stream, 'audio'))

  /* ------------------------------------------------------------------ *
   * Watches: browser permission
   * ------------------------------------------------------------------ */

  // The permission changes while the page is open (reset or allowed via the lock icon, blocked in the OS): blocked
  // stops the capture and marks the kind lost; allowed again (or reset to 'prompt') asks for the media again when the
  // kind is wanted but has no live stream. The bridge's devices need no browser permission
  const watchAccess = (kind: Kind) => {
    const access = kind === 'video' ? cameraAccess : microphoneAccess
    const enabled = kind === 'video' ? camEnabled : micEnabled
    const stream = kind === 'video' ? camStream : micStream
    const lost = kind === 'video' ? camLost : micLost
    const bridge = kind === 'video' ? androidCamSelected : androidMicSelected
    watch(access, async state => {
      if (!state || bridge.value) return
      if (state === 'denied') {
        stopStream(stream.value)
        lost.value = true
        useEventBus('stream:track:end').emit({name: `${kind}_capture`, status: 'denied'})
        return
      }
      const live = stream.value?.getTracks().some(track => track.readyState === 'live')
      if (!enabled.value || live) return
      try {
        // Also refreshes the device list: labels and ids stay empty until the permission is granted
        await ensurePermissions()
        lost.value = false
        await recover(kind)
      } catch (error) {
        console.error(`[streamStore] Requesting the ${kind} again failed:`, error)
      }
    })
  }
  watchAccess('video')
  watchAccess('audio')

  return {
    // camera
    camEnabled,
    camStream,
    camOn,
    camAvailable,
    camLost,
    camResolution,
    cameras,
    selectedCameraId,
    cameraAccess,
    camConstraint,
    isCamSupported,
    torchToggled,
    hasTorchCapability,
    startCam,
    restartCam,
    nextCamStream,
    toggleTorch,
    // microphone
    micEnabled,
    micStream,
    micOn,
    micAvailable,
    micLost,
    microphones,
    selectedMicId,
    microphoneAccess,
    isMicSupported,
    startMic,
    restartMic,
    // both
    permissionGranted,
    stopAll,
    recover,
    ensurePermissions,
    // android bridge
    androidConnected,
    androidDevices,
    connectAndroid,
    isAndroidDevice,
  }
})
